import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { ErrorBoundary, Skeleton } from '@babomrs/ui';
import { apiClient } from '@babomrs/api-client';
import { ClientAuthContext, type ClientUser } from './hooks/useClientAuth';
import { loginRequest } from './hooks/msalConfig';
import { PublicLayout } from './layouts/PublicLayout';
import { ClientAppShell } from './layouts/ClientAppShell';
import { ActiveEntityProvider } from './hooks/useActiveEntity';

// Public marketing pages
const PublicHome = lazy(() => import('./pages/public/HomePage'));
const AboutPage = lazy(() => import('./pages/public/AboutPage'));
const ServicesPage = lazy(() => import('./pages/public/ServicesPage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));

// Authenticated portal pages
const PortalHome = lazy(() => import('./pages/HomePage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const SigningPage = lazy(() => import('./pages/SigningPage'));
const SigningRequestDetail = lazy(() => import('./pages/SigningRequestDetail'));
const QueriesPage = lazy(() => import('./pages/QueriesPage'));
const QueryDetail = lazy(() => import('./pages/QueryDetail'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const BookAppointmentPage = lazy(() => import('./pages/BookAppointmentPage'));
const CommsPreferencesPage = lazy(() => import('./pages/CommsPreferencesPage'));
const ClientHelpCentrePage = lazy(() => import('./pages/ClientHelpCentrePage'));

// Standalone token-authenticated KYC questionnaire — rendered OUTSIDE the MSAL portal guard.
const KycPortalPage = lazy(() => import('./pages/aml/kyc/KycPortalPage'));

const isDevMode = import.meta.env.VITE_AUTH_MODE === 'dev' || import.meta.env.DEV;

export function App() {
  const [user, setUser] = useState<ClientUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (!isDevMode) {
      // Production: /me fetch happens after MSAL signs in (PortalLoader).
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const me = await apiClient.get<ClientUser>('/api/v1/client/me');
        if (!cancelled) setUser(me);
      } catch (err) {
        if (!cancelled && (err as { status?: number })?.status === 403) setIsPending(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const isAuthenticated = !!user;

  return (
    <ClientAuthContext.Provider value={{ user, isAuthenticated, isLoading }}>
      <ErrorBoundary>
        <BrowserRouter>
          <Routes>
            {/* Public marketing site */}
            <Route element={<PublicLayout isAuthenticated={isAuthenticated} />}>
              <Route index element={<PublicSuspense><PublicHome /></PublicSuspense>} />
              <Route path="/about" element={<PublicSuspense><AboutPage /></PublicSuspense>} />
              <Route path="/services" element={<PublicSuspense><ServicesPage /></PublicSuspense>} />
              <Route path="/contact" element={<PublicSuspense><ContactPage /></PublicSuspense>} />
            </Route>

            {/* Authenticated portal */}
            <Route
              path="/portal/*"
              element={<PortalRoutes user={user} setUser={setUser} isPending={isPending} setIsPending={setIsPending} />}
            />

            <Route path="/login" element={<LoginRedirect />} />

            {/* Standalone token-gated KYC questionnaire — no MSAL, outside /portal/* */}
            <Route path="/aml/kyc" element={<PublicSuspense><KycPortalPage /></PublicSuspense>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ErrorBoundary>
    </ClientAuthContext.Provider>
  );
}

function PublicSuspense({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<div className="max-w-6xl mx-auto px-4 sm:px-6 py-20"><Skeleton className="h-64 w-full" /></div>}>
      {children}
    </Suspense>
  );
}

function LoginRedirect() {
  const { instance } = useMsal();
  useEffect(() => {
    if (isDevMode) {
      window.location.href = '/portal';
      return;
    }
    instance.loginRedirect(loginRequest).catch((err) => console.error('Login failed:', err));
  }, [instance]);
  return null;
}

interface PortalRoutesProps {
  user: ClientUser | null;
  setUser: (u: ClientUser | null) => void;
  isPending: boolean;
  setIsPending: (b: boolean) => void;
}

function PortalRoutes({ user, setUser, isPending, setIsPending }: PortalRoutesProps) {
  if (isDevMode) {
    return user ? <AuthenticatedPortal /> : <PendingNotice variant={isPending ? 'pending' : 'notFound'} />;
  }
  return (
    <>
      <AuthenticatedTemplate>
        <PortalLoader user={user} setUser={setUser} setIsPending={setIsPending}>
          <AuthenticatedPortal />
        </PortalLoader>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignInRequired />
      </UnauthenticatedTemplate>
    </>
  );
}

/**
 * Exported for test: this component owns the decision about what a client is TOLD when their
 * account cannot be loaded, which is the behaviour worth pinning. `App` itself mounts its own
 * BrowserRouter, so rendering the whole app inside the test harness's MemoryRouter would nest two
 * routers and test nothing useful.
 */
export function PortalLoader({
  user, setUser, setIsPending, children,
}: {
  user: ClientUser | null;
  setUser: (u: ClientUser | null) => void;
  setIsPending: (b: boolean) => void;
  children: React.ReactNode;
}) {
  const [loading, setLoading] = useState(!user);
  // Null until we know WHY. Previously there was no such state and the render below passed a
  // hardcoded `isPending`, so every failure — 500, timeout, dead API — was reported to the client
  // as "Account Pending Activation".
  const [failure, setFailure] = useState<PortalLoadFailure | null>(null);
  // Bumping this re-runs the effect. Without it the fetch ran ONCE: a client who hit a transient
  // failure stayed on the error screen for ever, because nothing asked again even after the API
  // recovered. That is how a 90-second outage became an indefinitely stuck portal.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (user) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const me = await apiClient.get<ClientUser>('/api/v1/client/me');
        if (cancelled) return;
        setUser(me);
        setFailure(null);
      } catch (err) {
        if (cancelled) return;
        const status = (err as { status?: number })?.status;
        // 403 = the server knows the account and is withholding it (not yet activated).
        // 404 = the server looked and there is no such client.
        // ANYTHING ELSE — including a network failure, where status is undefined — is OUR
        // problem, not the client's, and is retryable.
        const kind: PortalLoadFailure =
          status === 403 ? 'pending' : status === 404 ? 'notFound' : 'error';
        setFailure(kind);
        // Kept in sync for the dev-mode branch, which renders from this flag.
        setIsPending(kind === 'pending');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, setUser, setIsPending, attempt]);

  if (loading) {
    return <div className="flex items-center justify-center h-screen"><Skeleton className="w-48 h-6" /></div>;
  }
  if (!user) {
    return (
      <PendingNotice
        variant={failure ?? 'error'}
        onRetry={() => setAttempt((n) => n + 1)}
      />
    );
  }
  return <>{children}</>;
}

function SignInRequired() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const handleSignIn = () => {
    instance.loginRedirect(loginRequest).catch((err) => console.error('Login failed:', err));
  };
  return (
    <div className="flex flex-col items-center justify-center min-h-screen text-center p-6 bg-white">
      <img src="/brand/ba-logo-original.jpg" alt="B&A Accounting Group" className="h-16 w-auto mb-6" />
      <h1 className="text-2xl font-bold text-brand-ink mb-3">Client Portal</h1>
      <p className="text-brand-charcoal max-w-md mb-8 leading-relaxed">
        Sign in with the email address your accountant invited &mdash; we&apos;ll email you a one-time code
        to enter. There is no password. The first time, you&apos;ll confirm your email and name.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={handleSignIn}
          className="px-6 py-3 rounded-md bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navy-deep transition-colors"
        >
          Sign in
        </button>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="px-6 py-3 rounded-md border border-brand-mute/60 text-brand-charcoal text-sm font-semibold hover:bg-brand-surface-2 transition-colors"
        >
          Back to website
        </button>
      </div>
    </div>
  );
}

/**
 * Why a client could not be loaded. THREE states, not two.
 *
 * `pending` and `notFound` are answers from the server about the ACCOUNT — the client should call
 * the firm. `error` means we could not ask the question at all, and the client should simply try
 * again. Collapsing the third into the first is the defect this type exists to prevent: it tells a
 * client with a perfectly good account that it is "being set up", which is false, sends them to
 * ring their accountant about nothing, and hides a platform outage behind a reassuring message.
 */
export type PortalLoadFailure = 'pending' | 'notFound' | 'error';

function PendingNotice({ variant, onRetry }: { variant: PortalLoadFailure; onRetry?: () => void }) {
  const copy = {
    pending: {
      title: 'Account Pending Activation',
      body: 'Your account is being set up. Please contact your accountant if you’ve already received an invitation.',
    },
    notFound: {
      title: 'Account Not Found',
      body: 'We couldn’t find your account. Please contact your accountant.',
    },
    // Deliberately says nothing about the account: we do not know anything about it. The client
    // is told what to DO, which is the one thing that actually helps here.
    error: {
      title: 'We couldn’t load your account',
      body: 'Something went wrong on our end — your account is fine. Please try again in a moment.',
    },
  }[variant];

  return (
    <div className="flex flex-col items-center justify-center min-h-screen text-center p-6 bg-white">
      <img src="/brand/ba-logo-original.jpg" alt="B&A Accounting Group" className="h-16 w-auto mb-6" />
      <h1 className="text-2xl font-bold text-brand-ink mb-3">{copy.title}</h1>
      <p className="text-brand-charcoal max-w-md leading-relaxed">{copy.body}</p>
      {variant === 'error' && onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-6 px-6 py-3 rounded-md bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navy-deep transition-colors"
        >
          Try again
        </button>
      )}
    </div>
  );
}

function AuthenticatedPortal() {
  const pageFallback = <Skeleton className="h-64 w-full" />;
  // Spec §7 — the active entity, inside PortalLoader (so the person is known) and around every portal route.
  return (
    <ActiveEntityProvider>
      <Routes>
        <Route element={<ClientAppShell />}>
          <Route index element={<Suspense fallback={pageFallback}><PortalHome /></Suspense>} />
          <Route path="onboarding" element={<Suspense fallback={pageFallback}><OnboardingPage /></Suspense>} />
          <Route path="signing" element={<Suspense fallback={pageFallback}><SigningPage /></Suspense>} />
          <Route path="signing/:id" element={<Suspense fallback={pageFallback}><SigningRequestDetail /></Suspense>} />
          <Route path="queries" element={<Suspense fallback={pageFallback}><QueriesPage /></Suspense>} />
          <Route path="queries/:id" element={<Suspense fallback={pageFallback}><QueryDetail /></Suspense>} />
          <Route path="documents" element={<Suspense fallback={pageFallback}><DocumentsPage /></Suspense>} />
          <Route path="book-appointment" element={<Suspense fallback={pageFallback}><BookAppointmentPage /></Suspense>} />
          <Route path="comms-preferences" element={<Suspense fallback={pageFallback}><CommsPreferencesPage /></Suspense>} />
          {/* Help Centre (#10803 AC5) — searchable index of every help topic. Static
              instruction only, no client data. */}
          <Route path="help" element={<Suspense fallback={pageFallback}><ClientHelpCentrePage /></Suspense>} />
          <Route path="*" element={<Navigate to="/portal" replace />} />
        </Route>
      </Routes>
    </ActiveEntityProvider>
  );
}
